// This module re-exports the @tanstack/react-router API with hand-written,
// app-facing signatures so every component that consumes routing is checked
// against the app's own known route paths (see src/lib/routes.ts) rather than
// the router's deeply-generic inferred types.
import {
  Link as RouterLink,
  Outlet as RouterOutlet,
  useNavigate as routerUseNavigate,
  useRouterState as routerUseRouterState,
  useParams as routerUseParams,
  useSearch as routerUseSearch,
} from '@tanstack/react-router';
import type { CSSProperties, FC, ReactNode, MouseEventHandler } from 'react';
import type { AppRoutePath, ParamsFor, SearchFor } from './routes';

/** Props accepted by the router `<Link>` as used across the app. */
export interface LinkProps {
  /** Only known route paths are navigable — see src/lib/routes.ts. */
  to: AppRoutePath;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  /** Props merged onto the anchor when its route is active. */
  activeProps?: { className?: string };
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

export const Link = RouterLink as FC<LinkProps>;

export const Outlet = RouterOutlet as FC;

/**
 * Options accepted by the `navigate` function returned from `useNavigate`.
 * Generic over the target path `P` so that `params` and `search` are checked
 * against the types that route actually declares (see src/lib/routes.ts).
 */
export interface NavigateOptions<P extends AppRoutePath = AppRoutePath> {
  to: P;
  params?: ParamsFor<P>;
  search?: SearchFor<P> | ((prev: SearchFor<P>) => SearchFor<P>);
  replace?: boolean;
}

export type NavigateFn = <P extends AppRoutePath>(options: NavigateOptions<P>) => void;

export const useNavigate = routerUseNavigate as () => NavigateFn;

/** The active location as read via `useRouterState`. */
export interface RouterLocation {
  pathname: string;
  search: Record<string, unknown>;
  hash: string;
}

export interface RouterState {
  location: RouterLocation;
}

export const useRouterState = routerUseRouterState as () => RouterState;

export const useParams = routerUseParams as <T = Record<string, string>>(opts?: {
  strict?: boolean;
}) => T;

export const useSearch = routerUseSearch as <T = Record<string, unknown>>(opts?: {
  strict?: boolean;
}) => T;
